import { skipToken } from "@reduxjs/toolkit/query";
import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useGetPatientVisitByVisitTypeQuery } from "src/api/services/onlineVisitsApi";

import { OnlineVisit, PatientSubscriptionsWithPatient } from "src/api/types";
import { useManageDrawer } from "src/hooks/useManageDrawer";
import IntakeDrawer from "src/pages/Cancellations/components/IntakeDrawer";

type ContextType = {
  viewActions: {
    handleOpen: () => void;
    handleClose: () => void;
  };
  subscription: PatientSubscriptionsWithPatient | null;
  handleOpenViewIntake: (
    subscription: PatientSubscriptionsWithPatient | null,
  ) => void;
  visit: OnlineVisit | undefined;
  loadingVisit: boolean;
};

export const ViewIntakeDrawerContext = createContext<ContextType>({
  viewActions: {
    handleClose: () => {},
    handleOpen: () => {},
  },
  subscription: null,
  handleOpenViewIntake: () => {},
  visit: undefined,
  loadingVisit: false,
});

interface Props {
  children: ReactNode;
}

const ViewIntakeProvider = ({ children }: Props) => {
  const [open, viewActions] = useManageDrawer();
  const [subscription, setSubscription] =
    useState<PatientSubscriptionsWithPatient | null>(null);

  const { data: visit, isLoading: loadingVisit } =
    useGetPatientVisitByVisitTypeQuery(
      subscription
        ? {
            patient_id: subscription.patient_id,
            product_slug: subscription.product.slug,
            visit_type: "Cancellation",
            status: "completed",
            ascending: "desc",
          }
        : skipToken,
    );

  const handleOpenViewIntake = useCallback(
    (subscription: PatientSubscriptionsWithPatient | null) => {
      setSubscription(subscription);
      viewActions.handleOpen();
    },
    [viewActions],
  );

  const value = useMemo(
    () => ({
      viewActions,
      subscription,
      handleOpenViewIntake,
      visit,
      loadingVisit,
    }),
    [handleOpenViewIntake, loadingVisit, subscription, viewActions, visit],
  );

  return (
    <ViewIntakeDrawerContext.Provider value={value}>
      {children}
      {subscription ? (
        <IntakeDrawer
          open={open}
          onClose={viewActions.handleClose}
          visit={visit}
          loadingVisit={loadingVisit}
        />
      ) : null}
    </ViewIntakeDrawerContext.Provider>
  );
};

export default ViewIntakeProvider;
